/**
 * stimMonitoringEntry — draft + persistence helpers for opening the canonical
 * monitoring entry modal from the stim monitoring section.
 *
 * The stim sheet is an editable read surface; all writes flow through the
 * shared `MonitoringEntryModal` (see issue #1949 for the canonical-flow
 * direction). Cycle-bound entries persist as one monitoring encounter. The
 * legacy TestResult writer remains for dates that are not attached to a cycle
 * during the migration.
 */

import {
  type BatchRetryLedger,
  BATCH_NO_WRITE,
  settleBatchWithLedgerOrThrow,
} from "@src/packages/care/batch-create";
import type {
  MonitoringEntryDraft,
  MonitoringPreviousResult,
} from "../suggested/MonitoringEntryModal";
import type {
  TestResult,
  CreateTestResultPayload,
  UpdateTestResultPayload,
} from "@src/packages/test-results/hooks/useTestResults";
import { createCycleMonitoringRequestId } from "@src/packages/care/monitoring/requestId";
import {
  type CreateCycleMonitoringEncounterPayload,
  type CycleMonitoringEncounter,
  type CycleMonitoringMeasurement,
  type UpdateCycleMonitoringEncounterPayload,
} from "@src/packages/care/hooks/useCycleMonitoring";

const P = {
  sizesLeft: /follicle\s*sizes?\s*left/i,
  sizesRight: /follicle\s*sizes?\s*right/i,
  countLeft: /follicle\s*count\s*left/i,
  countRight: /follicle\s*count\s*right/i,
  lining: /endometrial|lining|thickness/i,
  e2: /estradiol|^e2$/i,
  lh: /^lh$/i,
  fsh: /^fsh$/i,
  p4: /progesterone|^p4$/i,
  notes: /monitoring\s*notes/i,
};

const AUTO_ASSOCIATED_PATTERNS = [
  P.sizesLeft,
  P.sizesRight,
  P.countLeft,
  P.countRight,
  P.lining,
  P.e2,
  P.lh,
  P.fsh,
  P.p4,
];

/** Recognize recorded monitoring that may not yet be plottable (e.g. counts). */
export function hasRecordedMonitoringResult(
  result: Pick<TestResult, "name" | "date"> &
    Partial<Pick<TestResult, "doctorValue" | "patientValue" | "value">>,
): boolean {
  const value = result.doctorValue ?? result.patientValue ?? result.value ?? "";
  return (
    !!result.date &&
    value.trim() !== "" &&
    AUTO_ASSOCIATED_PATTERNS.some((pattern) => pattern.test(result.name ?? ""))
  );
}

export type MonitoringReadOptions = {
  /** The open cycle's start date, used for unlinked result association. */
  cycleStartDate?: string | null;
  /**
   * Allow monitoring-shaped, unlinked lab rows to project into this cycle.
   * This is only enabled for the currently open cycle; historical cycles must
   * rely on an explicit cycleId.
   */
  includeUnlinkedResults?: boolean;
};

export function isCycleRelevantMonitoringResult(
  result: Pick<TestResult, "name" | "date" | "cycleId">,
  cycleId: string | null | undefined,
  options: MonitoringReadOptions = {},
): boolean {
  if (cycleId === undefined) return true;
  if (result.cycleId === cycleId) return true;
  if (!options.includeUnlinkedResults || cycleId === null) return false;
  if (result.cycleId !== null || !options.cycleStartDate) {
    return false;
  }

  const dateISO = result.date?.split("T")[0];
  return (
    !!dateISO &&
    dateISO >= options.cycleStartDate &&
    AUTO_ASSOCIATED_PATTERNS.some((pattern) => pattern.test(result.name ?? ""))
  );
}

export function monitoringResultsForCycle(
  results: TestResult[] | undefined,
  cycleId: string | null | undefined,
  options: MonitoringReadOptions = {},
): TestResult[] {
  return (results ?? []).filter((result) =>
    isCycleRelevantMonitoringResult(result, cycleId, options),
  );
}

function findFor(
  results: TestResult[] | undefined,
  dateISO: string,
  pattern: RegExp,
  cycleId?: string | null,
  options: MonitoringReadOptions = {},
): TestResult | undefined {
  return results?.find(
    (r) =>
      r.date?.split("T")[0] === dateISO &&
      isCycleRelevantMonitoringResult(r, cycleId, options) &&
      pattern.test(r.name ?? ""),
  );
}

function resolved(r: TestResult | undefined): string {
  return r ? (r.doctorValue ?? r.patientValue ?? r.value ?? "") : "";
}

export function buildMonitoringDraft(
  results: TestResult[] | undefined,
  dateISO: string,
  cycleId?: string | null,
  options: MonitoringReadOptions = {},
): MonitoringEntryDraft {
  const e2 = findFor(results, dateISO, P.e2, cycleId, options);
  const lh = findFor(results, dateISO, P.lh, cycleId, options);
  const fsh = findFor(results, dateISO, P.fsh, cycleId, options);
  const p4 = findFor(results, dateISO, P.p4, cycleId, options);
  return {
    leftSizes: resolved(
      findFor(results, dateISO, P.sizesLeft, cycleId, options),
    ),
    rightSizes: resolved(
      findFor(results, dateISO, P.sizesRight, cycleId, options),
    ),
    leftCount: resolved(
      findFor(results, dateISO, P.countLeft, cycleId, options),
    ),
    rightCount: resolved(
      findFor(results, dateISO, P.countRight, cycleId, options),
    ),
    liningThickness: resolved(
      findFor(results, dateISO, P.lining, cycleId, options),
    ),
    estradiol: resolved(e2),
    estradiolUnit: e2?.unit ?? "pg/mL",
    lh: resolved(lh),
    lhUnit: lh?.unit ?? "mIU/mL",
    fsh: resolved(fsh),
    fshUnit: fsh?.unit ?? "mIU/mL",
    progesterone: resolved(p4),
    progesteroneUnit: p4?.unit ?? "ng/mL",
    notes: resolved(findFor(results, dateISO, P.notes, cycleId, options)),
  };
}

/**
 * Build the "previous scan" context from the most recent earlier date that
 * carries any monitoring data. Returns null when there is no prior day.
 */
export function buildPreviousMonitoring(
  results: TestResult[] | undefined,
  dateISO: string,
  cycleId?: string | null,
  options: MonitoringReadOptions = {},
): MonitoringPreviousResult | null {
  const earlierDates = new Set<string>();
  for (const r of results ?? []) {
    if (!isCycleRelevantMonitoringResult(r, cycleId, options)) continue;
    const d = r.date?.split("T")[0];
    if (d && d < dateISO) {
      earlierDates.add(d);
    }
  }
  if (earlierDates.size === 0) {
    return null;
  }
  const prevDate = [...earlierDates].sort().reverse()[0]!;
  const draft = buildMonitoringDraft(results, prevDate, cycleId, options);
  return {
    date: prevDate,
    leftSizes: draft.leftSizes,
    rightSizes: draft.rightSizes,
    leftCount: draft.leftCount,
    rightCount: draft.rightCount,
    liningThickness: draft.liningThickness,
    estradiol: draft.estradiol,
    estradiolUnit: draft.estradiolUnit,
    lh: draft.lh,
    lhUnit: draft.lhUnit,
    progesterone: draft.progesterone,
    progesteroneUnit: draft.progesteroneUnit,
  };
}

export const ENCOUNTER_MEASUREMENT_CODES = {
  leftSizes: "follicle_sizes_left",
  rightSizes: "follicle_sizes_right",
  leftCount: "follicle_count_left",
  rightCount: "follicle_count_right",
  liningThickness: "endometrial_thickness",
  estradiol: "estradiol",
  lh: "lh",
  fsh: "fsh",
  progesterone: "progesterone",
} as const;

const emptyMonitoringDraft = (): MonitoringEntryDraft => ({
  leftSizes: "",
  rightSizes: "",
  leftCount: "",
  rightCount: "",
  liningThickness: "",
  estradiol: "",
  estradiolUnit: "pg/mL",
  lh: "",
  lhUnit: "mIU/mL",
  fsh: "",
  fshUnit: "mIU/mL",
  progesterone: "",
  progesteroneUnit: "ng/mL",
  notes: "",
});

export function measurementValue(
  measurement: CycleMonitoringMeasurement,
): string {
  if (measurement.value === null) return "";
  return String(measurement.value);
}

export function encounterDate(
  encounter: CycleMonitoringEncounter,
): string | null {
  return (
    encounter.actualAt?.split("T")[0] ??
    encounter.scheduledAt?.split("T")[0] ??
    null
  );
}

export function findMonitoringEncounterForDate(
  encounters: CycleMonitoringEncounter[] | undefined,
  dateISO: string,
  cycleId?: string | null,
): CycleMonitoringEncounter | null {
  return (
    encounters?.find(
      (encounter) =>
        encounter.status !== "voided" &&
        encounterDate(encounter) === dateISO &&
        (cycleId === undefined || encounter.cycleId === cycleId),
    ) ?? null
  );
}

export function buildMonitoringDraftFromEncounter(
  encounter: CycleMonitoringEncounter,
): MonitoringEntryDraft {
  const draft = emptyMonitoringDraft();
  const byCode = new Map(
    encounter.measurements.map((measurement) => [
      measurement.code,
      measurement,
    ]),
  );
  const valueFor = (code: string) => {
    const measurement = byCode.get(code);
    return measurement ? measurementValue(measurement) : "";
  };
  const unitFor = (code: string, fallback: string) =>
    byCode.get(code)?.unit ?? fallback;

  draft.leftSizes = valueFor(ENCOUNTER_MEASUREMENT_CODES.leftSizes);
  draft.rightSizes = valueFor(ENCOUNTER_MEASUREMENT_CODES.rightSizes);
  draft.leftCount = valueFor(ENCOUNTER_MEASUREMENT_CODES.leftCount);
  draft.rightCount = valueFor(ENCOUNTER_MEASUREMENT_CODES.rightCount);
  draft.liningThickness = valueFor(ENCOUNTER_MEASUREMENT_CODES.liningThickness);
  draft.estradiol = valueFor(ENCOUNTER_MEASUREMENT_CODES.estradiol);
  draft.estradiolUnit = unitFor(
    ENCOUNTER_MEASUREMENT_CODES.estradiol,
    draft.estradiolUnit,
  );
  draft.lh = valueFor(ENCOUNTER_MEASUREMENT_CODES.lh);
  draft.lhUnit = unitFor(ENCOUNTER_MEASUREMENT_CODES.lh, draft.lhUnit);
  draft.fsh = valueFor(ENCOUNTER_MEASUREMENT_CODES.fsh);
  draft.fshUnit = unitFor(ENCOUNTER_MEASUREMENT_CODES.fsh, draft.fshUnit);
  draft.progesterone = valueFor(ENCOUNTER_MEASUREMENT_CODES.progesterone);
  draft.progesteroneUnit = unitFor(
    ENCOUNTER_MEASUREMENT_CODES.progesterone,
    draft.progesteroneUnit,
  );
  draft.notes = encounter.notes ?? "";
  return draft;
}

function valueForEncounterCode(
  draft: MonitoringEntryDraft,
  code: string,
): string {
  switch (code) {
    case ENCOUNTER_MEASUREMENT_CODES.leftSizes:
      return draft.leftSizes;
    case ENCOUNTER_MEASUREMENT_CODES.rightSizes:
      return draft.rightSizes;
    case ENCOUNTER_MEASUREMENT_CODES.leftCount:
      return draft.leftCount;
    case ENCOUNTER_MEASUREMENT_CODES.rightCount:
      return draft.rightCount;
    case ENCOUNTER_MEASUREMENT_CODES.liningThickness:
      return draft.liningThickness;
    case ENCOUNTER_MEASUREMENT_CODES.estradiol:
      return draft.estradiol;
    case ENCOUNTER_MEASUREMENT_CODES.lh:
      return draft.lh;
    case ENCOUNTER_MEASUREMENT_CODES.fsh:
      return draft.fsh;
    case ENCOUNTER_MEASUREMENT_CODES.progesterone:
      return draft.progesterone;
    default:
      return "";
  }
}

export function buildMonitoringDraftFromSources(
  encounters: CycleMonitoringEncounter[] | undefined,
  results: TestResult[] | undefined,
  dateISO: string,
  cycleId?: string | null,
  options: MonitoringReadOptions = {},
): MonitoringEntryDraft {
  const fallback = buildMonitoringDraft(results, dateISO, cycleId, options);
  const encounter = findMonitoringEncounterForDate(
    encounters,
    dateISO,
    cycleId,
  );
  if (!encounter) return fallback;

  const encounterDraft = buildMonitoringDraftFromEncounter(encounter);
  const measurementCodes = new Set(
    encounter.measurements.map((measurement) => measurement.code),
  );
  const encounterValue = (code: string, fallbackValue: string) =>
    measurementCodes.has(code)
      ? valueForEncounterCode(encounterDraft, code)
      : fallbackValue;

  return {
    ...fallback,
    leftSizes: encounterValue(
      ENCOUNTER_MEASUREMENT_CODES.leftSizes,
      fallback.leftSizes,
    ),
    rightSizes: encounterValue(
      ENCOUNTER_MEASUREMENT_CODES.rightSizes,
      fallback.rightSizes,
    ),
    leftCount: encounterValue(
      ENCOUNTER_MEASUREMENT_CODES.leftCount,
      fallback.leftCount,
    ),
    rightCount: encounterValue(
      ENCOUNTER_MEASUREMENT_CODES.rightCount,
      fallback.rightCount,
    ),
    liningThickness: encounterValue(
      ENCOUNTER_MEASUREMENT_CODES.liningThickness,
      fallback.liningThickness,
    ),
    estradiol: encounterValue(
      ENCOUNTER_MEASUREMENT_CODES.estradiol,
      fallback.estradiol,
    ),
    estradiolUnit: measurementCodes.has(ENCOUNTER_MEASUREMENT_CODES.estradiol)
      ? encounterDraft.estradiolUnit
      : fallback.estradiolUnit,
    lh: encounterValue(ENCOUNTER_MEASUREMENT_CODES.lh, fallback.lh),
    lhUnit: measurementCodes.has(ENCOUNTER_MEASUREMENT_CODES.lh)
      ? encounterDraft.lhUnit
      : fallback.lhUnit,
    fsh: encounterValue(ENCOUNTER_MEASUREMENT_CODES.fsh, fallback.fsh),
    fshUnit: measurementCodes.has(ENCOUNTER_MEASUREMENT_CODES.fsh)
      ? encounterDraft.fshUnit
      : fallback.fshUnit,
    progesterone: encounterValue(
      ENCOUNTER_MEASUREMENT_CODES.progesterone,
      fallback.progesterone,
    ),
    progesteroneUnit: measurementCodes.has(
      ENCOUNTER_MEASUREMENT_CODES.progesterone,
    )
      ? encounterDraft.progesteroneUnit
      : fallback.progesteroneUnit,
    notes: encounter.notes !== null ? encounterDraft.notes : fallback.notes,
  };
}

export type MonitoringDisplayResult = Pick<
  TestResult,
  | "cycleId"
  | "date"
  | "name"
  | "doctorValue"
  | "patientValue"
  | "value"
  | "unit"
  | "clinicalAssessment"
>;

const measurementNames: Record<string, string> = {
  [ENCOUNTER_MEASUREMENT_CODES.leftSizes]: "Follicle Sizes Left",
  [ENCOUNTER_MEASUREMENT_CODES.rightSizes]: "Follicle Sizes Right",
  [ENCOUNTER_MEASUREMENT_CODES.leftCount]: "Follicle Count Left",
  [ENCOUNTER_MEASUREMENT_CODES.rightCount]: "Follicle Count Right",
  [ENCOUNTER_MEASUREMENT_CODES.liningThickness]: "Endometrial Thickness",
  [ENCOUNTER_MEASUREMENT_CODES.estradiol]: "E2",
  [ENCOUNTER_MEASUREMENT_CODES.lh]: "LH",
  [ENCOUNTER_MEASUREMENT_CODES.fsh]: "FSH",
  [ENCOUNTER_MEASUREMENT_CODES.progesterone]: "Progesterone",
};

export function monitoringEncounterDisplayResults(
  encounters: CycleMonitoringEncounter[] | undefined,
  cycleId?: string | null,
): MonitoringDisplayResult[] {
  return (encounters ?? [])
    .filter(
      (encounter) =>
        encounter.status !== "voided" &&
        (cycleId === undefined || encounter.cycleId === cycleId),
    )
    .flatMap((encounter) => {
      const date = encounterDate(encounter);
      if (!date) return [];
      return encounter.measurements.flatMap((measurement) => {
        const code = measurement.code;
        if (!code) return [];
        const name = measurementNames[code];
        if (!name) return [];
        const value = measurementValue(measurement);
        const isClinicSource = [
          "provider_entered",
          "clinic_imported",
          "system",
          "document_parse",
        ].includes(measurement.sourceKind);
        return [
          {
            cycleId: encounter.cycleId,
            date,
            name,
            doctorValue: isClinicSource ? value : null,
            patientValue: isClinicSource ? null : value,
            value: null,
            unit: measurement.unit ?? null,
            clinicalAssessment: null,
          },
        ];
      });
    });
}

export function monitoringDisplayResultKey(result: {
  date: string | null | undefined;
  name: string | null | undefined;
}): string {
  return `${result.date?.split("T")[0] ?? ""}:${result.name ?? ""}`;
}

export function buildPreviousMonitoringFromSources(
  encounters: CycleMonitoringEncounter[] | undefined,
  results: TestResult[] | undefined,
  dateISO: string,
  cycleId?: string | null,
  options: MonitoringReadOptions = {},
): MonitoringPreviousResult | null {
  const dates = new Set<string>();
  for (const encounter of encounters ?? []) {
    const date = encounterDate(encounter);
    if (
      date &&
      date < dateISO &&
      encounter.status !== "voided" &&
      (cycleId === undefined || encounter.cycleId === cycleId)
    ) {
      dates.add(date);
    }
  }
  for (const result of results ?? []) {
    if (!isCycleRelevantMonitoringResult(result, cycleId, options)) continue;
    const date = result.date?.split("T")[0];
    if (date && date < dateISO) dates.add(date);
  }
  const previousDate = [...dates].sort().reverse()[0];
  if (!previousDate) return null;

  const draft = buildMonitoringDraftFromSources(
    encounters,
    results,
    previousDate,
    cycleId,
    options,
  );
  return {
    date: previousDate,
    leftSizes: draft.leftSizes,
    rightSizes: draft.rightSizes,
    leftCount: draft.leftCount,
    rightCount: draft.rightCount,
    liningThickness: draft.liningThickness,
    estradiol: draft.estradiol,
    estradiolUnit: draft.estradiolUnit,
    lh: draft.lh,
    lhUnit: draft.lhUnit,
    progesterone: draft.progesterone,
    progesteroneUnit: draft.progesteroneUnit,
  };
}

type EncounterMeasurementInput = {
  code: string;
  category: CycleMonitoringMeasurement["category"];
  value: string;
  unit: string | null;
};

const encounterMeasurementInputs = (
  draft: MonitoringEntryDraft,
): EncounterMeasurementInput[] => [
  {
    code: ENCOUNTER_MEASUREMENT_CODES.leftSizes,
    category: "ultrasound",
    value: draft.leftSizes,
    unit: "mm",
  },
  {
    code: ENCOUNTER_MEASUREMENT_CODES.rightSizes,
    category: "ultrasound",
    value: draft.rightSizes,
    unit: "mm",
  },
  {
    code: ENCOUNTER_MEASUREMENT_CODES.leftCount,
    category: "ultrasound",
    value: draft.leftCount,
    unit: null,
  },
  {
    code: ENCOUNTER_MEASUREMENT_CODES.rightCount,
    category: "ultrasound",
    value: draft.rightCount,
    unit: null,
  },
  {
    code: ENCOUNTER_MEASUREMENT_CODES.liningThickness,
    category: "ultrasound",
    value: draft.liningThickness,
    unit: "mm",
  },
  {
    code: ENCOUNTER_MEASUREMENT_CODES.estradiol,
    category: "bloodwork",
    value: draft.estradiol,
    unit: draft.estradiolUnit,
  },
  {
    code: ENCOUNTER_MEASUREMENT_CODES.lh,
    category: "bloodwork",
    value: draft.lh,
    unit: draft.lhUnit,
  },
  {
    code: ENCOUNTER_MEASUREMENT_CODES.fsh,
    category: "bloodwork",
    value: draft.fsh,
    unit: draft.fshUnit,
  },
  {
    code: ENCOUNTER_MEASUREMENT_CODES.progesterone,
    category: "bloodwork",
    value: draft.progesterone,
    unit: draft.progesteroneUnit,
  },
];

function localTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

function localNoonISO(dateISO: string): string {
  return new Date(`${dateISO}T12:00:00`).toISOString();
}

function measurementIdFor(
  existing: CycleMonitoringEncounter | null,
  code: string,
): string {
  return (
    existing?.measurements.find((measurement) => measurement.code === code)
      ?.id ?? createCycleMonitoringRequestId()
  );
}

export function buildCycleMonitoringEncounterPayload(
  draft: MonitoringEntryDraft,
  dateISO: string,
  existing: CycleMonitoringEncounter | null,
  clientRequestId: string,
  sourceDocumentIds: string[] = [],
): Omit<CreateCycleMonitoringEncounterPayload, "measurements"> & {
  measurements: NonNullable<
    CreateCycleMonitoringEncounterPayload["measurements"]
  >;
} {
  const linkedDocumentIds = [
    ...(existing?.sourceDocumentIds ?? []),
    ...sourceDocumentIds,
  ];
  const measurements = encounterMeasurementInputs(draft)
    .filter((measurement) => measurement.value.trim().length > 0)
    .map((measurement) => ({
      id: measurementIdFor(existing, measurement.code),
      category: measurement.category,
      code: measurement.code,
      value: measurement.value.trim(),
      unit: measurement.unit,
      reference: null,
      sourceKind: "patient_reported" as const,
      sourceRecordId: null,
      sourceDocumentId: null,
      patientConfirmedAt: null,
      notes: null,
    }));

  return {
    planSnapshotId: existing?.planSnapshotId ?? null,
    phaseId: existing?.phaseId ?? null,
    status: "awaiting_review",
    reviewStatus: "pending",
    scheduledAt: existing?.scheduledAt ?? null,
    actualAt: existing?.actualAt ?? localNoonISO(dateISO),
    timeZone: existing?.timeZone ?? localTimeZone(),
    measurements,
    notes: draft.notes.trim() || null,
    clinicianReviewedAt: null,
    sourceKind: "patient_reported",
    sourceRecordId: null,
    sourceDocumentIds: [...new Set(linkedDocumentIds)],
    clientRequestId,
  };
}

export async function persistMonitoringEncounter(opts: {
  draft: MonitoringEntryDraft;
  dateISO: string;
  existing: CycleMonitoringEncounter | null;
  clientRequestId: string;
  sourceDocumentIds?: string[];
  mutations: {
    create: (
      payload: CreateCycleMonitoringEncounterPayload,
    ) => Promise<unknown>;
    update: (payload: {
      encounterId: string;
      data: UpdateCycleMonitoringEncounterPayload;
    }) => Promise<unknown>;
  };
}): Promise<void> {
  const {
    draft,
    dateISO,
    existing,
    clientRequestId,
    sourceDocumentIds = [],
    mutations,
  } = opts;
  const payload = buildCycleMonitoringEncounterPayload(
    draft,
    dateISO,
    existing,
    clientRequestId,
    sourceDocumentIds,
  );

  if (existing) {
    await mutations.update({
      encounterId: existing.id,
      data: {
        ...payload,
        expectedVersion: existing.version,
      } as UpdateCycleMonitoringEncounterPayload,
    });
    return;
  }
  await mutations.create(payload);
}

export type LatestMonitoringSummary = {
  /** ISO date (YYYY-MM-DD) of the most recent day carrying monitoring data */
  date: string;
  /** Estradiol value for that day, when present */
  estradiol: string | null;
  /** Total follicle count (left + right) for that day, 0 when absent */
  follicleCount: number;
};

/**
 * Latest monitoring snapshot from the canonical encounter source plus the
 * compatible legacy/unlinked test-result projection. Summary surfaces must
 * agree with the flowsheet and monitoring entry modal (#1949).
 */
export function buildLatestMonitoringSummary(
  results: TestResult[] | undefined,
  cycleId?: string | null,
  encounters?: CycleMonitoringEncounter[],
  options: MonitoringReadOptions = {},
): LatestMonitoringSummary | null {
  const encounterResults = monitoringEncounterDisplayResults(
    encounters,
    cycleId,
  );
  const scopedResults = monitoringResultsForCycle(results, cycleId, options);
  const dates = new Set<string>([
    ...encounterResults.flatMap((result) =>
      result.date ? [result.date.split("T")[0]!] : [],
    ),
    ...scopedResults.flatMap((result) =>
      result.date &&
      AUTO_ASSOCIATED_PATTERNS.some((pattern) =>
        pattern.test(result.name ?? ""),
      )
        ? [result.date.split("T")[0]!]
        : [],
    ),
  ]);
  if (dates.size === 0) return null;
  const latest = [...dates].sort().reverse()[0]!;
  const draft = buildMonitoringDraftFromSources(
    encounters,
    scopedResults,
    latest,
    cycleId,
    options,
  );
  const count = (value: string | null) => {
    const parsed = Number.parseInt(value ?? "", 10);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  return {
    date: latest,
    // buildMonitoringDraft resolves absent values to "" — normalize so
    // consumers can null-check instead of rendering an empty label.
    estradiol: draft.estradiol || null,
    follicleCount: count(draft.leftCount) + count(draft.rightCount),
  };
}

type Mutations = {
  create: (body: CreateTestResultPayload) => Promise<unknown>;
  update: (arg: {
    testResultId: string;
    data: UpdateTestResultPayload;
  }) => Promise<unknown>;
  remove: (testResultId: string) => Promise<unknown>;
};

/**
 * Receipt for one biomarker write: the id of the row now holding this
 * value (null when the write removed the row). An edited retry updates
 * this row instead of skipping or re-creating (#2236 review).
 */
export type MonitoringWriteReceipt = { id: string | null };

/** Pull the created row's id out of the create-mutation response envelope. */
function createdTestResultId(result: unknown): string | null {
  if (typeof result !== "object" || result === null) return null;
  const data = (result as { data?: unknown }).data;
  if (typeof data !== "object" || data === null) return null;
  const id = (data as { id?: unknown }).id;
  return typeof id === "string" ? id : null;
}

type BiomarkerSpec = {
  name: string;
  pattern: RegExp;
  value: string;
  unit: string | null;
  type: "blood" | "imaging" | "clinical";
  hormonal?: boolean;
  ultrasound?: boolean;
};

export async function persistMonitoringDraft(opts: {
  draft: MonitoringEntryDraft;
  dateISO: string;
  cycleId: string | null | undefined;
  results: TestResult[] | undefined;
  ledger: BatchRetryLedger<MonitoringWriteReceipt>;
  mutations: Mutations;
}): Promise<void> {
  const { draft, dateISO, cycleId, results, ledger, mutations } = opts;

  const specs: BiomarkerSpec[] = [
    {
      name: "Follicle Sizes Left",
      pattern: P.sizesLeft,
      value: draft.leftSizes,
      unit: "mm",
      type: "imaging",
      ultrasound: true,
    },
    {
      name: "Follicle Sizes Right",
      pattern: P.sizesRight,
      value: draft.rightSizes,
      unit: "mm",
      type: "imaging",
      ultrasound: true,
    },
    {
      name: "Follicle Count Left",
      pattern: P.countLeft,
      value: draft.leftCount,
      unit: null,
      type: "imaging",
      ultrasound: true,
    },
    {
      name: "Follicle Count Right",
      pattern: P.countRight,
      value: draft.rightCount,
      unit: null,
      type: "imaging",
      ultrasound: true,
    },
    {
      name: "Endometrial Thickness",
      pattern: P.lining,
      value: draft.liningThickness,
      unit: "mm",
      type: "imaging",
      ultrasound: true,
    },
    {
      name: "E2",
      pattern: P.e2,
      value: draft.estradiol,
      unit: draft.estradiolUnit,
      type: "blood",
      hormonal: true,
    },
    {
      name: "LH",
      pattern: P.lh,
      value: draft.lh,
      unit: draft.lhUnit,
      type: "blood",
      hormonal: true,
    },
    {
      name: "FSH",
      pattern: P.fsh,
      value: draft.fsh,
      unit: draft.fshUnit,
      type: "blood",
      hormonal: true,
    },
    {
      name: "Progesterone",
      pattern: P.p4,
      value: draft.progesterone,
      unit: draft.progesteroneUnit,
      type: "blood",
      hormonal: true,
    },
    {
      name: "Monitoring Notes",
      pattern: P.notes,
      value: draft.notes,
      unit: null,
      type: "clinical",
    },
  ];

  // Settle per biomarker against a receipt ledger: an identical accepted
  // write is skipped, an EDITED value re-runs with the accepted row's id so
  // it updates that row (never skips the new value, never creates a
  // duplicate while the query cache catches up), and blank no-ops are not
  // remembered at all (#2236 + review).
  await settleBatchWithLedgerOrThrow(
    specs,
    (spec) => ({
      key: `${dateISO}:${cycleId ?? ""}:${spec.name}`,
      signature: `${spec.value.trim()}|${spec.unit ?? ""}`,
    }),
    async (
      spec,
      _index,
      prior,
    ): Promise<MonitoringWriteReceipt | typeof BATCH_NO_WRITE> => {
      const existing = findFor(results, dateISO, spec.pattern, cycleId);
      const trimmed = spec.value.trim();
      // The row this biomarker's accepted write lives in, whether it came
      // from the (possibly stale) query cache or from this save session.
      const targetId = existing?.id ?? prior?.id ?? null;

      if (trimmed) {
        if (targetId) {
          await mutations.update({
            testResultId: targetId,
            data: { patientValue: trimmed, unit: spec.unit },
          });
          return { id: targetId };
        }
        const body: CreateTestResultPayload = {
          name: spec.name,
          patientValue: trimmed,
          unit: spec.unit,
          date: dateISO,
          cycleId: cycleId ?? null,
          type: spec.type,
          ...(spec.hormonal ? { bloodTestCategory: "hormonal" } : {}),
          ...(spec.ultrasound
            ? { imagingTestCategory: "transvaginal_ultrasound" }
            : {}),
        };
        const created = await mutations.create(body);
        return { id: createdTestResultId(created) };
      }

      if (existing) {
        // Preserve clinic-authored values; only clear the patient layer.
        if (existing.doctorValue != null || existing.value != null) {
          await mutations.update({
            testResultId: existing.id,
            data: { patientValue: null },
          });
          return { id: existing.id };
        }
        await mutations.remove(existing.id);
        return { id: null };
      }

      if (prior?.id) {
        // Row created earlier in this save session, blanked before retry:
        // it is patient-authored by construction, so remove it.
        await mutations.remove(prior.id);
        return { id: null };
      }

      return BATCH_NO_WRITE;
    },
    ledger,
    (outcome) =>
      `Failed to save ${outcome.failed.length} monitoring value(s); ${outcome.succeeded.length} saved`,
  );
}
